// src/components/modules/ModuleList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { fetchModules, deleteModule, updateModule } from "./ModuleApi";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faEdit,
  faPlus,
  faCircleCheck,
  faCircleXmark,
  faChevronDown,
  faChevronUp,
  faArrowsRotate,
  faSortAlphaDown,
  faSortAlphaUp,
  faCheckDouble,
  faBan,
  faCloudUploadAlt,
} from "@fortawesome/free-solid-svg-icons";

/** Keep this in sync with your ModuleDTO on the backend */
type UUID = string;
export interface ModuleRow {
  id: UUID;
  name: string;
  description: string;
  status: boolean;

  // Core
  canCreate: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReport: boolean;

  // Extended
  canAssign: boolean;
  canComment: boolean;
  canUpload: boolean;
  canApprove: boolean;
  canReject: boolean;
  canChangeStatus: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canExport: boolean;
  canImport: boolean;
  canShare: boolean;

  // UI flags
  visibleInSidebar: boolean;
  visibleInDashboard: boolean;

  // audit (optional)
  createdBy?: UUID;
  updatedBy?: UUID;
  createdAt?: string;
  updatedAt?: string;
}

/** Compute a union of only the boolean keys of ModuleRow */
type BooleanKeys<T> = {
  [K in keyof T]-?: T[K] extends boolean ? K : never
}[keyof T];
type ModuleBooleanKey = BooleanKeys<ModuleRow>;

/** All boolean flag keys you want Select All / Unselect All to toggle/persist */
const TOGGLE_KEYS: ModuleBooleanKey[] = [
  // core
  "canCreate", "canView", "canEdit", "canDelete", "canReport",
  // extended
  "canAssign", "canComment", "canUpload", "canApprove", "canReject",
  "canChangeStatus", "canArchive", "canRestore", "canExport", "canImport", "canShare",
  // ui flags
  "visibleInSidebar", "visibleInDashboard",
];

const YesNoBadge: React.FC<{ value: boolean }> = ({ value }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
      value
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-red-50 text-red-700 border-red-200"
    }`}
  >
    <FontAwesomeIcon icon={value ? faCircleCheck : faCircleXmark} />
    {value ? "Yes" : "No"}
  </span>
);

/** Small checkbox cell that edits a boolean flag and marks row dirty */
const ToggleCell: React.FC<{
  row: ModuleRow;
  field: ModuleBooleanKey;
  onChange: (id: string, field: ModuleBooleanKey, next: boolean) => void;
}> = ({ row, field, onChange }) => (
  <td className="p-3">
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        className="w-4 h-4 accent-indigo-600"
        checked={!!row[field]}
        onChange={(e) => onChange(row.id, field, e.target.checked)}
      />
      <YesNoBadge value={!!row[field]} />
    </label>
  </td>
);

type SortDir = "asc" | "desc";

const ModuleList: React.FC = () => {
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // track per-row "dirty" (changed) & "saving" state
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string | null>>({});

  const loadModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchModules();
      const rows: ModuleRow[] = (res?.data ?? res) as ModuleRow[];
      setModules(rows || []);
      setDirty({});
      setRowError({});
    } catch (e: any) {
      setError(e?.message || "Failed to fetch modules");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this module?")) return;
    await deleteModule(id);
    loadModules();
  };

  useEffect(() => {
    loadModules();
  }, []);

  /** Derived sorted list by module name */
  const sortedModules = useMemo(() => {
    const copy = [...modules];
    copy.sort((a, b) => {
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      if (an < bn) return sortDir === "asc" ? -1 : 1;
      if (an > bn) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [modules, sortDir]);

  /** Toggle sorting direction when clicking the Name header */
  const toggleSort = () => {
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  /** Compute “all selected” state for a module */
  const isAllSelected = (m: ModuleRow) => TOGGLE_KEYS.every((k) => !!m[k]);

  /** Check if some flags are true and some false (for indeterminate) */
  const isPartiallySelected = (m: ModuleRow) => {
    const trues = TOGGLE_KEYS.filter((k) => !!m[k]).length;
    return trues > 0 && trues < TOGGLE_KEYS.length;
  };

  /** Apply a value to all toggled keys for a given module (type-safe patch) */
  const setAllFlagsFor = (id: string, value: boolean) => {
    setModules((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const patch = TOGGLE_KEYS.reduce((acc, key) => {
          acc[key] = value;
          return acc;
        }, {} as Record<ModuleBooleanKey, boolean>);
        return { ...row, ...patch };
      })
    );
    setDirty((d) => ({ ...d, [id]: true }));
  };

  /** Toggle a single field and mark row dirty */
  const handleFieldToggle = (id: string, field: ModuleBooleanKey, next: boolean) => {
    setModules((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: next } : row))
    );
    setDirty((d) => ({ ...d, [id]: true }));
  };

  /** Persist one row to backend */
  const applyRow = async (id: string) => {
    const row = modules.find((m) => m.id === id);
    if (!row) return;
    try {
      setSaving((s) => ({ ...s, [id]: true }));
      setRowError((e) => ({ ...e, [id]: null }));

      // Construct a minimal payload: only booleans + status
      const payload: Partial<ModuleRow> = TOGGLE_KEYS.reduce((acc, k) => {
        acc[k] = row[k];
        return acc;
      }, {} as Partial<ModuleRow>);
      payload.status = row.status;

      // Call API
      await updateModule(id, payload);

      // Clear dirty & saving flags on success
      setDirty((d) => ({ ...d, [id]: false }));
    } catch (e: any) {
      setRowError((r) => ({
        ...r,
        [id]: e?.message || "Failed to update module",
      }));
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  const coreHeaders = useMemo(
    () => ["Create", "View", "Edit", "Delete", "Report"],
    []
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Modules</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={loadModules}
            className="border rounded px-3 py-2 text-slate-700 hover:bg-slate-50"
            title="Reload"
          >
            <FontAwesomeIcon icon={faArrowsRotate} className="mr-2" />
            Reload
          </button>
          <Link
            to="/admin/modules/create"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Module
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      )}

      <div className="w-full bg-white shadow-md rounded overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-100">
            <tr className="text-sm">
              <th className="p-3 w-6"></th>
              <th className="p-3">
                <button
                  type="button"
                  onClick={toggleSort}
                  className="inline-flex items-center gap-2 font-medium hover:underline"
                  title={`Sort by name (${sortDir === "asc" ? "A→Z" : "Z→A"})`}
                >
                  Name
                  <FontAwesomeIcon
                    icon={sortDir === "asc" ? faSortAlphaDown : faSortAlphaUp}
                    className="text-slate-600"
                  />
                </button>
              </th>
              <th className="p-3">Description</th>
              <th className="p-3">
                <label className="inline-flex items-center gap-2">
                  <span>Status</span>
                </label>
              </th>
              {/* Core CRUD up front (as toggles) */}
              {coreHeaders.map((h) => (
                <th key={h} className="p-3">{h}</th>
              ))}
              {/* UI flags */}
              <th className="p-3">Sidebar</th>
              <th className="p-3">Dashboard</th>
              {/* Per-row Select All controls */}
              <th className="p-3">Select All</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedModules.map((m) => {
              const isOpen = !!expanded[m.id];
              const allSelected = isAllSelected(m);
              const partial = isPartiallySelected(m);
              const isDirty = !!dirty[m.id];
              const isSaving = !!saving[m.id];
              const errMsg = rowError[m.id];

              return (
                <React.Fragment key={m.id}>
                  <tr className="border-t align-top">
                    <td className="p-3">
                      <button
                        onClick={() =>
                          setExpanded((s) => ({ ...s, [m.id]: !s[m.id] }))
                        }
                        className="border rounded px-2 py-1 text-slate-700 hover:bg-slate-50"
                        title={isOpen ? "Hide details" : "Show details"}
                      >
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                      </button>
                    </td>

                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 text-slate-600">{m.description}</td>

                    {/* Status toggle */}
                    <td className="p-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600"
                          checked={!!m.status}
                          onChange={(e) => handleFieldToggle(m.id, "visibleInDashboard" as any, !!m.visibleInDashboard) /* noop to mark dirty via a real field below */}
                          onClick={(e) => {
                            // status isn't part of TOGGLE_KEYS but is persisted in payload
                            setModules((prev) =>
                              prev.map((row) =>
                                row.id === m.id ? { ...row, status: !row.status } : row
                              )
                            );
                            setDirty((d) => ({ ...d, [m.id]: true }));
                          }}
                        />
                        <YesNoBadge value={!!m.status} />
                      </label>
                    </td>

                    {/* Core CRUD as editable toggles */}
                    <ToggleCell row={m} field="canCreate" onChange={handleFieldToggle} />
                    <ToggleCell row={m} field="canView" onChange={handleFieldToggle} />
                    <ToggleCell row={m} field="canEdit" onChange={handleFieldToggle} />
                    <ToggleCell row={m} field="canDelete" onChange={handleFieldToggle} />
                    <ToggleCell row={m} field="canReport" onChange={handleFieldToggle} />

                    {/* UI flags */}
                    <ToggleCell row={m} field="visibleInSidebar" onChange={handleFieldToggle} />
                    <ToggleCell row={m} field="visibleInDashboard" onChange={handleFieldToggle} />

                    {/* Select All controls */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {/* Checkbox: checked = all true, indeterminate = some true */}
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = partial && !allSelected;
                          }}
                          onChange={(e) => setAllFlagsFor(m.id, e.target.checked)}
                          title="Select/Unselect all flags for this module"
                        />
                        {/* Optional quick buttons */}
                        <button
                          type="button"
                          className="text-xs border rounded px-2 py-1 hover:bg-indigo-50 text-indigo-700 border-indigo-200"
                          onClick={() => setAllFlagsFor(m.id, true)}
                          title="Select all flags"
                        >
                          <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
                          All
                        </button>
                        <button
                          type="button"
                          className="text-xs border rounded px-2 py-1 hover:bg-slate-50 text-slate-700 border-slate-200"
                          onClick={() => setAllFlagsFor(m.id, false)}
                          title="Unselect all flags"
                        >
                          <FontAwesomeIcon icon={faBan} className="mr-1" />
                          None
                        </button>
                      </div>
                    </td>

                    {/* Row actions */}
                    <td className="p-3 space-x-3 whitespace-nowrap">
                      {/* Update / Apply (enabled only if dirty) */}
                      <button
                        type="button"
                        disabled={!isDirty || isSaving}
                        onClick={() => applyRow(m.id)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border ${
                          isDirty && !isSaving
                            ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                            : "bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed"
                        }`}
                        title="Apply changes to this module"
                      >
                        <FontAwesomeIcon icon={faCloudUploadAlt} />
                        {isSaving ? "Saving…" : "Update"}
                      </button>

                      <Link
                        to={`/admin/modules/edit/${m.id}`}
                        className="text-blue-600 hover:text-blue-800 ml-2"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        onClick={() => handleDelete(m.id)}
                        className="text-red-600 hover:text-red-800 ml-2"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>

                      {/* Row-level error (inline) */}
                      {errMsg && (
                        <div className="text-red-600 text-xs mt-1">{errMsg}</div>
                      )}
                    </td>
                  </tr>

                  {/* Collapsible extended permissions (editable) */}
                  {isOpen && (
                    <tr className="border-t bg-slate-50/50">
                      <td className="p-3" />
                      <td className="p-3" colSpan={11}>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-semibold mb-2 text-slate-700">
                              Extended Actions
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {([
                                "canAssign",
                                "canComment",
                                "canUpload",
                                "canApprove",
                                "canReject",
                                "canChangeStatus",
                                "canArchive",
                                "canRestore",
                                "canExport",
                                "canImport",
                                "canShare",
                              ] as ModuleBooleanKey[]).map((key) => (
                                <label
                                  key={key}
                                  className="inline-flex items-center gap-2 text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-indigo-600"
                                    checked={!!m[key]}
                                    onChange={(e) =>
                                      handleFieldToggle(m.id, key, e.target.checked)
                                    }
                                  />
                                  <span className="capitalize">
                                    {key.replace(/([A-Z])/g, " $1")}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold mb-2 text-slate-700">
                              UI Visibility
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {(["visibleInSidebar", "visibleInDashboard"] as ModuleBooleanKey[]).map(
                                (key) => (
                                  <label
                                    key={key}
                                    className="inline-flex items-center gap-2 text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 accent-indigo-600"
                                      checked={!!m[key]}
                                      onChange={(e) =>
                                        handleFieldToggle(m.id, key, e.target.checked)
                                      }
                                    />
                                    <span className="capitalize">
                                      {key.replace(/([A-Z])/g, " $1")}
                                    </span>
                                  </label>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3" />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {sortedModules.length === 0 && !loading && (
              <tr>
                <td colSpan={14} className="p-6 text-center text-slate-500">
                  No modules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="mt-3 text-slate-600 text-sm">Loading modules…</div>
      )}
    </div>
  );
};

export default ModuleList;